const GENERIC_MESSAGE =
  'No pudimos completar la solicitud. Inténtalo de nuevo.';

/**
 * Forma mínima de un error HTTP tal como llega al store. Se comprueba
 * estructuralmente porque el error llega tipado como `unknown` y puede no venir
 * de la capa HTTP; para los que sí vienen de ella, `mapWhatsAppOtpError` solo
 * necesita `status` y `error`, no la clase completa.
 */
interface HttpErrorLike {
  readonly status: number;
  readonly error: unknown;
}

function isHttpErrorLike(error: unknown): error is HttpErrorLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as Record<string, unknown>)['status'] === 'number'
  );
}

/**
 * Códigos de error que el backend devuelve en el cuerpo de la respuesta para
 * los endpoints `/api/auth/whatsapp/*`.
 */
type WhatsAppOtpErrorCode =
  | 'invalid_code'
  | 'invalid_or_expired_token'
  | 'invalid_phone'
  | 'email_already_registered'
  | 'delivery_failed';

/**
 * Extrae el código de error del cuerpo de la respuesta sin asumir una forma
 * estricta — el backend lo expone como `error_code`, con fallback a `error`,
 * `code` o `message`.
 */
function extractErrorCode(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) {
    return undefined;
  }

  const record = body as Record<string, unknown>;

  // El backend duplica el código: en la raíz y anidado bajo `error`.
  // `{ message, code, error: { error_code }, error_code }`
  const nested = record['error'];
  const nestedCode =
    typeof nested === 'object' && nested !== null
      ? (nested as Record<string, unknown>)['error_code']
      : nested;

  const candidate =
    record['error_code'] ?? nestedCode ?? record['code'] ?? record['message'];

  return typeof candidate === 'string' ? candidate : undefined;
}

/**
 * Mapea un error HTTP del flujo WhatsApp OTP a un mensaje uniforme y
 * **no-enumerante**: el código inválido y los errores de teléfono comparten un
 * mismo texto para no revelar si un número está o no registrado.
 */
export function mapWhatsAppOtpError(error: unknown): string {
  if (!isHttpErrorLike(error)) {
    return GENERIC_MESSAGE;
  }

  switch (error.status) {
    case 422: {
      const code = extractErrorCode(error.error) as
        | WhatsAppOtpErrorCode
        | undefined;

      if (code === 'email_already_registered') {
        return 'Este email ya pertenece a otra cuenta.';
      }

      if (code === 'invalid_or_expired_token') {
        return 'La verificación expiró. Solicita un nuevo código e inténtalo de nuevo.';
      }

      // Contrato seatix-api#842: Laravel rechaza un teléfono implausible
      // (validación propia o `400 invalid_phone` firmado de la función) con
      // `422 { error_code: 'invalid_phone' }`. Es un problema de formato, no de
      // enumeración: se muestra un mensaje accionable para corregir el número.
      if (code === 'invalid_phone') {
        return 'Revisa tu número de teléfono e inténtalo de nuevo.';
      }

      if (code === 'delivery_failed') {
        return 'No pudimos entregarte el código por WhatsApp ni SMS. Intenta con otro método.';
      }

      // invalid_code y el resto de validaciones comparten un mensaje
      // no-enumerante.
      return 'El código es inválido o expiró. Verifícalo e inténtalo de nuevo.';
    }
    case 429:
      return 'Demasiados intentos. Espera un momento antes de volver a intentar.';
    case 503:
      return 'El servicio de verificación no está disponible. Inténtalo más tarde.';
    default:
      return GENERIC_MESSAGE;
  }
}

/**
 * Distingue el caso `delivery_failed` (agotados WhatsApp y SMS) del resto de
 * errores 422 — es la única señal que habilita la sugerencia de método
 * alterno en el diálogo de OTP.
 */
export function isWhatsAppDeliveryFailedError(error: unknown): boolean {
  if (!isHttpErrorLike(error) || error.status !== 422) {
    return false;
  }

  return extractErrorCode(error.error) === 'delivery_failed';
}
